/*
 * KAPSTAT — investigator sign-in / sign-up pages (/signin, /signup).
 * Participants never use these; the research team signs in here to reach /console.
 * Copyright (c) 2026 Eddlynn Edtech Private Limited.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isConfigured, signIn, signUp } from './lib/analytics';
import { Wordmark } from './Wordmark.jsx';
import './console.css';

export default function Auth({ mode = 'signin' }) {
  const signup = mode === 'signup';
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setErr(null); setMsg(null); setBusy(true);
    try {
      if (signup) {
        await signUp(email, pw);
        setMsg('Account created. Confirm via the email link, then ask the study lead to grant your access. Once granted, sign in.');
      } else {
        await signIn(email, pw);
        navigate('/console');
      }
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="console auth">
      <div className="auth__bg" aria-hidden="true" />
      <div className="auth__card">
        <Link to="/" className="auth__brand"><Wordmark height={28} /></Link>
        <div className="auth__eyebrow">{signup ? 'Create account' : 'Investigator access'}</div>
        <h1 className="auth__title">{signup ? 'Create investigator account' : 'Welcome back'}</h1>
        <p className="auth__sub">For the research team only. Participants never sign in.</p>

        {!isConfigured && <div className="cnote">Backend not configured yet — add Supabase keys and redeploy.</div>}

        <form onSubmit={submit} className="auth__form">
          <label className="auth__field">
            <span className="auth__label">Email</span>
            <input className="cinput" type="email" placeholder="you@institution.org" value={email}
                   onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus />
          </label>
          <label className="auth__field">
            <span className="auth__label">Password</span>
            <input className="cinput" type="password" placeholder="Min. 6 characters" value={pw}
                   onChange={(e) => setPw(e.target.value)} required minLength={6}
                   autoComplete={signup ? 'new-password' : 'current-password'} />
          </label>
          <button className="cbtn auth__submit" type="submit" disabled={busy}>
            {busy ? 'Please wait…' : (signup ? 'Create account' : 'Sign in')}
          </button>
        </form>

        {msg && <div className="cnote">{msg}</div>}
        {err && <div className="cerr">{err}</div>}

        <p className="auth__alt">
          {signup
            ? <>Already have an account? <Link className="clink" to="/signin">Sign in</Link></>
            : <>Need an account? <Link className="clink" to="/signup">Sign up</Link></>}
        </p>
        <Link className="auth__back" to="/">← Back to site</Link>
      </div>
    </div>
  );
}
